<template>
  <v-stepper-content :step="currentCategory.orderIndex"
                     :class="$style.stepperContent">

    <div :class="$style.orderMain">
      <div :class="$style.orderPrice">Item Price {{ price | toPrice }}</div>
      <div :class="$style.orderMainInfo">
        <div :class="$style.backBtnContainer">
          <BaseButton :class="[$style.orderBackBtn,
                               {[$style.hide] : first}]"
                      button-style="Back"
                      @click="previousStep">
            <svg v-svg
                 :class="$style.backIcon"
                 symbol="straight-arrow"/>
            <span>Back</span>
          </BaseButton>
        </div>


        <div :class="$style.orderMainCenter">
          <OrderPreview
            :current-category-key="categoryKey"
          />
        </div>

        <div :class="$style.nextBtnContainer">
          <BaseButton v-if="last"
                      :class="[$style.orderNextBtn,
                               {[$style.fullWidth] : first}]"
                      :disabled="nextDisabled"
                      button-style="Main"
                      @click="nextStep">
            <span>Done</span>
          </BaseButton>
          <BaseButton v-else
                      :class="[$style.orderNextBtn,
                               {[$style.fullWidth] : first}]"
                      :disabled="nextDisabled"
                      button-style="Main"
                      @click="nextStep">
            <span>Next</span>
            <svg v-svg
                 :class="$style.nextIcon"
                 symbol="straight-arrow"/>
          </BaseButton>
        </div>
      </div>

      <div
        v-if="!shouldHideStep"
        :class="$style.orderMainStep"
      >
        Step {{ currentCategory.orderIndex }}
      </div>
      <div :class="$style.orderMainTitle">
        <h2>Choose Your
          <span :class="$style.orderTitleName">
            {{ currentCategory.name }}
          </span>
          <span v-if="currentCategoryQuantity > 0"
                :class="$style.orderTitleNumber">
            {{ currentCategoryQuantity }}
          </span>
        </h2>
        <p v-if="currentCategory.note"
           :class="$style.categoryNote">
          {{ currentCategory.note }}
        </p>
      </div>

      <div :class="$style.configuratorGrid">

        <ConfiguratorOption v-for="(itemKey, index) in sortedItems"
                            :key="`${index}-option`"
                            :item-key="itemKey"
                            :category-key="categoryKey"
                            :class="configuratorItemStyle"
                            @selected="itemSelected"/>

        <ConfiguratorHelpButton v-if="!isDrinkProduct"/>
      </div>

    </div>

    <ConfiguratorMobileButtons :first="first"
                               :last="last"
                               :done-disable="doneDisabled"
                               :next-disable="nextDisabled"
                               @next="nextStep"
                               @back="previousStep"/>

    <LoadingSpinner v-if="isLoading"/>
  </v-stepper-content>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import BaseButton from '@/components/BaseButton';
import ConfiguratorMobileButtons from '@/apps/configurator/components/ConfiguratorMobileButtons';
import ConfiguratorHelpButton from '@/apps/configurator/components/ConfiguratorHelpButton';
import LoadingSpinner from '@/apps/configurator/components/LoadingSpinner';
import LoadingMixin from '@/lib/mixins/loading-mixin';
import { CART_CALC_ORDER, CART_PREPARE_ORDER, CART_EXPAND } from '@/apps/cart/store/types';
import { PRODUCTS_ID, CATEGORY_ID } from '@/config/constants';
import { FriesMenu } from '@/apps/menu/constants';
import { Getter, Action } from 'vuex-class';
import ConfiguratorOption from './ConfiguratorOption';
import { CONFIGURATOR_UPDATE_ITEM, SHOW_HELP } from '../store/types';
import OrderPreview from './Preview/OrderPreview';
import OrderFinisherMixin from '../mixins/order-finisher-mixin';

/**
 * Container for configurator step content like options and current
 * burger rendering.
 *
 * Props:
 * @property {any} categoryKey - key for category saved in store.
 * @property {boolean} last - defines if this step is last.
 * @property {boolean} first - defines if this step is first.
 * @property {number} currentStep - number of the current step for navigation
 *                                  between configurator steps.
 */
@Component({
  name:  'ConfiguratorStepContent',
  props: {
    categoryKey:    {},
    last:           {},
    first:          {},
    currentStep:    {},
    currentProduct: {},
    lockNextStep:   {
      type:    Boolean,
      default: false,
    },
  },
  components: {
    LoadingSpinner,
    ConfiguratorHelpButton,
    BaseButton,
    ConfiguratorOption,
    ConfiguratorMobileButtons,
    OrderPreview,
  },
  mixins: [
    LoadingMixin,
    OrderFinisherMixin,
  ],
})
export default class ConfiguratorStepContent extends Vue {
  @Getter currentProductKey;
  @Getter categories;
  @Getter isMobilePlatform;
  @Action('CART_DISPLAY') displayCart;
  @Action(SHOW_HELP) showHelp;
  @Getter orderItemId;
  @Getter orderItemPrice;
  @Getter itemSelectedInCategory;
  @Getter isAuthenticated;
  @Getter order;
  @Getter isGuest;
  @Getter isKioskPlatform;
  /**
   * Retrieves category from vuex by key.
   */
  @Getter categories;

  /**
   * Get item from vuex by key.
   */
  @Getter itemByKey;

  /**
   * Retrieve order item from vuex.
   */
  @Getter orderItem;

  /**
   * Change order item data in state.
   */
  @Action(CONFIGURATOR_UPDATE_ITEM) addItem;

  @Action(CART_CALC_ORDER) cartCalcOrder;
  @Action(CART_PREPARE_ORDER) cartPrepareOrder;
  @Action(CART_EXPAND) cartExpand;

  get emptySelect() {
    const orderItem = this.orderItem[this.categoryKey] || {};
    const selectedItemsCount = Object.keys(orderItem).filter(i => i !== 'radio').length;

    return !this.currentCategoryQuantity && selectedItemsCount >= 1;
  }

  get price() {
    return this.orderItemPrice(this.orderItemId);
  }

  /**
   * Category "No" item.
   */
  get noItem() {
    const noItem = this.currentCategory.items.find(item => this.itemByKey(item).name.startsWith('No'));

    return noItem || null;
  }

  /**
   * Should the 'Next' button be disabled.
   */
  get nextDisabled() {
    if (this.noItem || this.itemSelectedInCategory(this.categoryKey)) return false;

    return true;
  }

  /**
   * Emit event to route configurator to the next step.
   */
  async nextStep() {
    if (!this.itemSelectedInCategory(this.categoryKey)) {
      this.autoSelectNo();
    }

    if (this.last) {
      try {
        this.finishOrder();
      } catch (error) {
        console.error(error);
      }

      this.$router.push({ name: 'menu' });
      this.displayCart(true);

      if (this.isMobilePlatform) {
        this.cartExpand();
      }

      return;
    }

    // If user has already added fries he should not be asked about adding them and we should skip this step after premium toppings
    const premiumToppingsStep = 6;

    if (this.currentStep === premiumToppingsStep
     && this.orderItem[CATEGORY_ID.BUNDLE_FRIES]) {
      this.$emit('stepChange', this.currentStep + 2);

      return;
    }

    this.$emit('stepChange', this.currentStep + 1);
  }

  /**
   * Auto `Select No` choice in next step If no selection is made on .
   */
  autoSelectNo() {
    if (!this.noItem) return;

    const itemData = {
      quantity: 1,
      itemKey:  this.noItem,
      modifier: null,
    };
    const categoryChange = Object.assign(
      { categoryKey: this.categoryKey },
      itemData,
    );

    this.addItem(categoryChange);
  }

  /**
   * Emit event to route configurator to the previous step.
   */
  previousStep() {
    this.$emit('stepChange', this.currentStep - 1);
  }

  /**
   * Retrieves selected category from state.
   * @returns {{
   * name: string,
   * orderIndex: number,
   * widget: string,
   * subProduct: any,
   * items: [any],
   * }}
   */
  get currentCategory() {
    return this.categories[this.categoryKey];
  }

  get isDrinkProduct() {
    return this.currentProductKey === PRODUCTS_ID.DRINKS;
  }

  /**
   * Sort items for current category by order index.
   */
  get sortedItems() {
    return this.currentCategory.items
      .sort((a, b) => {
        const itemA = this.itemByKey(a);
        const itemB = this.itemByKey(b);

        return itemA.orderIndex - itemB.orderIndex;
      });
  }

  /**
   * Count how many items selected in category
   */
  get currentCategoryQuantity() {
    const categoryData = this.orderItem[this.categoryKey];
    let quantity = 0;

    if (!categoryData) {
      return 0;
    }

    Object.values(categoryData).forEach((item) => {
      quantity += item.quantity;
    });

    return quantity || 0;
  }

  get configuratorItemStyle() {
    if (this.currentCategory.name === 'sauce' || this.currentCategory.name === 'fry dipper') {
      return this.$style.sauceConfiguratorItem;
    }

    return this.$style.configuratorItem;
  }

  /**
   * Mutate order item with new data category's item data.
   * @param itemData - Item data with quantity and modifications.
   */
  itemSelected(itemData) {
    const categoryChange = Object.assign(
      { categoryKey: this.categoryKey },
      itemData,
    );

    this.addItem(categoryChange);

    if (this.categoryKey === CATEGORY_ID.BOSS_FRIES
      && !this.itemSelectedInCategory(CATEGORY_ID.FRIES_CHEESE)) {
      const baseItem = this.itemByKey(Number(FriesMenu.CHEESE_ITEM));

      /** Use setTimeout as in other case, configurator watcher for changedItem get only last value. */
      setTimeout(() => this.addItem({
        itemKey:     FriesMenu.CHEESE_ITEM,
        categoryKey: baseItem.categoryKey,
        modifier:    null,
        quantity:    itemData.quantity,
      }), 0);
    }
  }

  get shouldHideStep() {
    // If this is the last and first step, it is the only one, therefore, no need to display it
    return this.last && this.first;
  }
}
</script>

<style lang="scss" module>
  @import "~@/apps/configurator/styles/step-content.scss";
</style>



// WEBPACK FOOTER //
// src/apps/configurator/components/ConfiguratorStepContent.vue